<script setup>
import { ChevronDownIcon } from '@heroicons/vue/16/solid'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import Pagination from '@/components/Pagination.vue'
import { index } from '@/actions/App/Http/Controllers/ProjectController'

defineProps({
    projects: {
        type: Object,
        required: true,
    },

    services: {
        type: Array,
        required: true,
    },

    filters: {
        type: Object,
        required: false,
        default: () => ({}),
    },
})
</script>

<template>
    <Head title="Projekty">
        <meta
            name="description"
            content="Prohlédněte si naše realizované projekty - vodoinstalace, topenářství a plynoinstalace. Reference a ukázky práce firmy Kalianko."
        />
    </Head>

    <section class="relative isolate overflow-hidden bg-gray-900 py-24 sm:py-32">
        <div class="from-brand-primary/20 absolute inset-0 -z-10 bg-linear-to-br via-gray-900 to-gray-900" />

        <div
            class="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-gray-900/80 shadow-xl ring-1 ring-white/10 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center"
        />

        <div class="section-width relative px-2 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Naše realizace</h1>
                <p class="mt-6 text-lg/8 text-gray-300">
                    Prohlédněte si ukázky naší práce. Každý projekt je pro nás výzvou a příležitostí ukázat naše
                    řemeslné dovednosti.
                </p>
            </div>
        </div>
    </section>

    <!-- Projects Grid -->
    <section class="section-width px-2 py-16 sm:py-24 lg:px-0">
        <div class="flex items-center justify-between pb-6">
            <h2 class="heading-2">Projekty</h2>
            <p class="text-sm text-gray-500">
                Celkem <span class="font-semibold text-gray-900">{{ projects.meta.total }}</span> projektů
            </p>
        </div>

        <div>
            <!-- Mobile Select -->
            <div class="grid grid-cols-1 sm:hidden">
                <select
                    aria-label="Vyberte kategorii"
                    class="focus:ring-brand-primary col-start-1 row-start-1 w-full appearance-none rounded-xl border-0 bg-white py-3 pr-10 pl-4 text-base font-medium text-gray-900 shadow-sm ring-1 ring-gray-200 focus:ring-2"
                    @change="
                        $inertia.visit(index().url + ($event.target.value ? '?service=' + $event.target.value : ''))
                    "
                >
                    <option
                        value=""
                        :selected="!filters.service"
                    >
                        Všechny projekty
                    </option>
                    <option
                        v-for="tab in services"
                        :key="tab.slug"
                        :value="tab.slug"
                        :selected="filters.service === tab.slug"
                    >
                        {{ tab.name }} ({{ tab.relationships.projects_count }})
                    </option>
                </select>
                <ChevronDownIcon
                    class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400"
                    aria-hidden="true"
                />
            </div>

            <!-- Desktop Tabs -->
            <div class="hidden sm:block">
                <nav class="flex w-fit flex-wrap items-center gap-1.5 rounded-full bg-gray-200 p-2">
                    <Link
                        :href="index().url"
                        :class="[
                            filters.service === undefined
                                ? 'bg-brand-primary text-white'
                                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 hover:ring-gray-300',
                            'rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200',
                        ]"
                        preserve-scroll
                        preserve-state
                    >
                        Vše
                    </Link>

                    <Link
                        v-for="tab in services"
                        :key="tab.slug"
                        :href="index().url + '?service=' + tab.slug"
                        :class="[
                            filters.service === tab.slug
                                ? 'bg-brand-primary text-white'
                                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 hover:ring-gray-300',
                            'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200',
                        ]"
                        preserve-scroll
                        preserve-state
                    >
                        {{ tab.name }}
                        <span
                            :class="[
                                filters.service === tab.slug ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600',
                                'inline-flex size-6 items-center justify-center rounded-full text-xs font-bold',
                            ]"
                        >
                            {{ tab.relationships.projects_count }}
                        </span>
                    </Link>
                </nav>
            </div>
        </div>

        <div class="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div
                v-for="project in projects.data"
                :key="project.id"
                class="group"
            >
                <ProjectCard :project="project" />
            </div>
        </div>

        <div
            v-if="projects.meta.last_page > 1"
            class="mt-16"
        >
            <Pagination :meta="projects.meta" />
        </div>
    </section>
</template>
